import { useSentryMutation, useSentryQuery } from '@/utils/sentryQueryWrapper';
import { ErrorMessageResponse } from '@/types/error';
import { BulkActionResponse } from '@/types/order';
import {
  bulkDelayRtoOrders,
  bulkResendOrders,
  delayRtoOrder,
  fetchInventoryOrders,
  fetchReattempt,
  resendOrder,
  scheduleReattempt,
} from '@/api/inventoryService';
import {
  BulkDelayRtoPayload,
  BulkResendPayload,
  InventoryOrdersResponse,
  ReattemptResponse,
  ResendPayload,
  ResendResponse,
} from '@/types/inventory';

export const useFetchInventoryOrders = (page: number, perPage: number, enabled: boolean = true) => {
  return useSentryQuery<InventoryOrdersResponse, ErrorMessageResponse>(
    ['inventory', page, perPage],
    () => fetchInventoryOrders(page, perPage),
    { enabled },
  );
};

export const useResendOrder = () => {
  return useSentryMutation<
    ResendResponse,
    ErrorMessageResponse,
    { uid: string; payload: ResendPayload }
  >(({ uid, payload }) => resendOrder(uid, payload));
};

export const useDelayRtoOrder = () => {
  return useSentryMutation<unknown, ErrorMessageResponse, { uid: string; reason?: string }>(
    ({ uid, reason }) => delayRtoOrder(uid, reason),
  );
};

export const useBulkResendOrders = () => {
  return useSentryMutation<BulkActionResponse, ErrorMessageResponse, BulkResendPayload>((payload) =>
    bulkResendOrders(payload),
  );
};

export const useBulkDelayRtoOrders = () => {
  return useSentryMutation<unknown, ErrorMessageResponse, BulkDelayRtoPayload>((payload) =>
    bulkDelayRtoOrders(payload),
  );
};

export const useReattempt = (uid: string, enabled: boolean = true) => {
  return useSentryQuery<ReattemptResponse, ErrorMessageResponse>(
    ['inventory-reattempt', uid],
    () => fetchReattempt(uid),
    // Silent: the badge is a hint, a failed read must not toast on page load.
    { enabled: enabled && !!uid, defaultErrorMessage: '' },
  );
};

export const useScheduleReattempt = () => {
  return useSentryMutation<
    ReattemptResponse,
    ErrorMessageResponse,
    { uid: string; reattemptAt: string }
  >(({ uid, reattemptAt }) => scheduleReattempt(uid, reattemptAt));
};
